/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState } from 'react'
import { useServiceContext } from '../root'
import Modal from './Modal'

interface ModalInfoProps {
  titleMsg: string
  msg: string
  modalStatus: () => void
  /**
   * Callback when confirm button is clicked (for Yes/No modals)
   */
  okFn?: () => void | Promise<void>
  /**
   * Callback when cancel button is clicked (for Yes/No modals)
   */
  cancelFn?: () => void
  /**
   * Whether to show cancel button (enables Yes/No mode)
   */
  showCancel?: boolean
  /**
   * Text for confirm button (default: 'OK' or 'YES' if showCancel)
   */
  okTxt?: string
  /**
   * Text for cancel button (default: 'NO')
   */
  cancelTxt?: string
  /**
   * Disable OK button when clicked to prevent double-clicks
   */
  disableOkBtnWhenClicked?: boolean
}

const ModalInfo: React.FC<ModalInfoProps> = ({
  titleMsg,
  msg,
  modalStatus,
  okFn,
  cancelFn,
  showCancel = false,
  okTxt,
  cancelTxt,
  disableOkBtnWhenClicked = false
}) => {
  const context = useServiceContext()
  const [isProcessing, setIsProcessing] = useState(false)

  const handleOk = async () => {
    if (disableOkBtnWhenClicked) {
      setIsProcessing(true)
    }

    try {
      if (okFn) {
        await okFn()
      }
      modalStatus()
    } catch (error) {
      console.error('Error in modal OK action:', error)
      if (disableOkBtnWhenClicked) {
        setIsProcessing(false)
      }
    }
  }

  const handleCancel = () => {
    if (cancelFn) {
      cancelFn()
    }
    modalStatus()
  }

  const defaultOkText = showCancel ? 'YES' : 'OK'
  const okButtonText = okTxt || defaultOkText
  const cancelButtonText = cancelTxt || 'NO'

  return (
    <Modal show={true} handleClose={handleCancel} size="md" title={context.service.filter.translate(titleMsg)}>
      <Modal.Body>
        <div
          dangerouslySetInnerHTML={{
            __html: context.service.filter.translate(msg)
          }}
        />
      </Modal.Body>
      <Modal.Footer>
        {showCancel && (
          <button type="button" className="btn btn-default" onClick={handleCancel} disabled={isProcessing}>
            {context.service.filter.translate(cancelButtonText)}
          </button>
        )}
        <button type="button" className="btn btn-primary" onClick={handleOk} disabled={isProcessing}>
          {isProcessing ? (
            <>
              <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
              {context.service.filter.translate('PROCESSING')}
            </>
          ) : (
            context.service.filter.translate(okButtonText)
          )}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default ModalInfo
